import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useGames } from '../hooks/useGames';
import { useActivePick } from '../hooks/useActivePick';
import GameCard from '../components/GameCard';
import GameFilters from '../components/GameFilters';
import Countdown from '../components/Countdown';
import LoadingSpinner from '../components/LoadingSpinner';
import { getPickStatusLabel, getNext3amET } from '../utils/formatters';
import { LEAGUES } from '../utils/constants';
import './GamesPage.css';

export default function GamesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { games: allGames, loading, error, refetch } = useGames();

  // Persist search & filter in URL so they survive navigate(-1)
  const activeFilter = searchParams.get('filter') || 'all';
  const searchQuery = searchParams.get('q') || '';

  const setActiveFilter = (filter) => {
    setSearchParams((prev) => {
      if (filter === 'all') prev.delete('filter');
      else prev.set('filter', filter);
      return prev;
    }, { replace: true });
  };

  const setSearchQuery = (query) => {
    setSearchParams((prev) => {
      if (!query) prev.delete('q');
      else prev.set('q', query);
      return prev;
    }, { replace: true });
  };
  const { activePick } = useActivePick();
  const [activePickGame, setActivePickGame] = useState(null);

  const hasLockedPick = activePick?.status === 'locked';

  // Real-time listener for the active pick's game (for live score updates)
  useEffect(() => {
    if (!activePick?.gameId) {
      setActivePickGame(null);
      return;
    }
    const unsub = onSnapshot(doc(db, 'games', activePick.gameId), (snap) => {
      if (snap.exists()) {
        setActivePickGame({ id: snap.id, ...snap.data() });
      }
    });
    return () => unsub();
  }, [activePick?.gameId]);

  // Derive which leagues actually have games today
  const activeLeagueSlugs = useMemo(() => {
    const slugs = new Set();
    allGames.forEach((g) => slugs.add(g.leagueSlug));
    return slugs;
  }, [allGames]);

  // Build filter tabs — only show leagues with games
  const sportFilters = useMemo(() => {
    const filters = [{ key: 'all', label: 'All Sports' }];
    Object.values(LEAGUES).forEach((l) => {
      if (activeLeagueSlugs.has(l.slug)) {
        filters.push({ key: l.slug, label: `${l.emoji} ${l.name}` });
      }
    });
    return filters;
  }, [activeLeagueSlugs]);

  // Filter games by sport and search query
  const filteredGames = useMemo(() => {
    let result = allGames;

    // Sport filter
    if (activeFilter !== 'all') {
      result = result.filter((g) => g.leagueSlug === activeFilter);
    }

    // Search filter — match against team names and abbreviations
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(
        (g) =>
          g.homeTeam.name.toLowerCase().includes(query) ||
          g.awayTeam.name.toLowerCase().includes(query) ||
          g.homeTeam.abbreviation.toLowerCase().includes(query) ||
          g.awayTeam.abbreviation.toLowerCase().includes(query)
      );
    }

    return result;
  }, [allGames, activeFilter, searchQuery]);

  // Reset filter if current filter has no games (e.g. after re-sync)
  if (activeFilter !== 'all' && !activeLeagueSlugs.has(activeFilter)) {
    setActiveFilter('all');
  }

  return (
    <div className="games-page">
      <h1>Upcoming Games</h1>

      {hasLockedPick && (
        <div className="active-pick-card">
          <h2>Your Active Pick</h2>
          <div className="active-pick-details">
            <div className="active-pick-team">
              {activePick.pickTeamLogo && (
                <img src={activePick.pickTeamLogo} alt="" className="active-pick-logo" />
              )}
              <div className="active-pick-info">
                <span className="active-pick-name">{activePick.pickTeamName}</span>
                <span className="active-pick-vs">vs {activePick.opponentTeamName}</span>
                <span className="active-pick-league">
                  {LEAGUES[activePick.leagueSlug]?.emoji} {activePick.league}
                </span>
              </div>
            </div>
            <div className="active-pick-status">
              {activePickGame && (activePickGame.status === 'in_progress' || activePickGame.status === 'final') ? (
                <>
                  <span className="active-pick-score">
                    {activePickGame.awayTeam?.abbreviation} {activePickGame.awayScore ?? 0}
                    {' '}&ndash;{' '}
                    {activePickGame.homeTeam?.abbreviation} {activePickGame.homeScore ?? 0}
                  </span>
                  {activePickGame.statusDetail &&
                    !activePickGame.statusDetail.toLowerCase().includes('scheduled') && (
                    <span className="active-pick-time">{activePickGame.statusDetail}</span>
                  )}
                </>
              ) : (
                <span className={`status-badge status-${activePick.status}`}>
                  {getPickStatusLabel(activePick.status)}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <LoadingSpinner message="Loading games..." />
      ) : error ? (
        <div className="games-error">{error}</div>
      ) : allGames.length === 0 ? (
        <div className="games-empty">
          <p>All games for today have finished. Check back tomorrow for a new slate!</p>
          <div className="next-games-countdown">
            <span className="next-games-label">New games in</span>
            <Countdown targetTime={getNext3amET()} onComplete={refetch} />
          </div>
        </div>
      ) : (
        <>
          <GameFilters
            sportFilters={sportFilters}
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            gameCount={filteredGames.length}
          />

          {filteredGames.length === 0 ? (
            <div className="games-empty">
              <p>No games match your search.</p>
            </div>
          ) : (
            <div className="games-list">
              {filteredGames.map((game) => (
                <GameCard
                  key={game.gameId}
                  game={game}
                  activePick={activePick}
                  disabled={hasLockedPick}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
